import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { fetchThemeData } from '../admin/themeSlice';
import { setSession } from '../admin/sessionSlice';
import { setCompanyLogoUrl } from './uiSlice';
import { isBlankLogoValue, sanitizeCompanyLogoUrl, sanitizeUserCompanyLogo } from '../functions/companyLogoUrl';
import { sendReport, extractReportIdFromResponse, resolveReportsAuthToken } from '../functions/sendReport';
import { checkHealthSprintPlayedApi, isHealthSprintAlreadyCompleted } from '../functions/checkHealthSprintPlayed';

const USER_STORAGE_KEY = 'hs_user_v1';

function readUserFromSessionStorage() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(USER_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return stripReportIdIfSameAsUserId(sanitizeUserCompanyLogo(parsed));
  } catch {
    return null;
  }
}

function writeUserToSessionStorage(user) {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
  } catch {
    /* ignore */
  }
}

function clearUserFromSessionStorage() {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(USER_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function shouldHydrateFromSavedSession() {
  if (typeof window === 'undefined') return false;
  try {
    const q = new URLSearchParams(window.location.search);
    const hasOtp = q.get('otp') != null && String(q.get('otp')).trim() !== '';
    // Never hydrate old user when a fresh OTP login is present.
    if (hasOtp) return false;
    // Any other in-app load (including a plain page refresh on /rules,
    // /health-sprint, /thankyou, etc.) should resume the saved session —
    // Redux state is in-memory only and is otherwise lost on refresh,
    // leaving those pages permanently blank (they never redirect to /login).
    return true;
  } catch {
    return false;
  }
}

const isTrueQueryParam = (value) =>
  typeof value === 'string' && value.trim().toLowerCase() === 'true';

const uuid4 = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };

/** True when `id` can be treated as report id (not the same value as user id). */
function idIsDistinctReportId(id, userId) {
  if (id == null || String(id).trim() === '') return false;
  if (userId == null || String(userId).trim() === '') return true;
  return String(id).trim() !== String(userId).trim();
}

/** Never store login `id` / mistaken fields as report id when they equal the real user id (breaks sendReport PUT). */
function rejectReportIdIfUserId(reportIdCandidate, resolvedUserId) {
  if (reportIdCandidate == null || String(reportIdCandidate).trim() === '') return null;
  const s = String(reportIdCandidate).trim();
  const uid = resolvedUserId != null ? String(resolvedUserId).trim() : '';
  if (uid !== '' && s === uid) return null;
  return s;
}

/** Guest/org login payloads differ; pull report id from every shape we’ve seen. */
function pickReportIdFromLoginPayload(d, resolvedUserId) {
  if (!d || typeof d !== 'object') return null;
  const r = d.report;
  const ur = d.userReport;
  const inner = d.data && typeof d.data === 'object' ? d.data : null;
  const candidates = [
    d.reportId,
    d.report_id,
    d.reportid,
    d.gameReportId,
    d.game_report_id,
    d.userReportId,
    d.user_report_id,
    r && typeof r === 'object' ? r.id ?? r.reportId : null,
    ur && typeof ur === 'object' ? ur.id ?? ur.reportId : null,
    inner
      ? inner.reportId ??
        inner.report_id ??
        inner.reportid ??
        inner.gameReportId ??
        inner.game_report_id ??
        (idIsDistinctReportId(inner.id, inner.userId) ? inner.id : null)
      : null,
    idIsDistinctReportId(d.id, d.userId) ? d.id : null,
  ];
  for (const v of candidates) {
    const ok = rejectReportIdIfUserId(v, resolvedUserId);
    if (ok) return ok;
  }
  return null;
}

function stripReportIdIfSameAsUserId(user) {
  if (!user || typeof user !== 'object') return user;
  const uid = user.userId != null ? String(user.userId).trim() : '';
  const rid = user.reportId != null ? String(user.reportId).trim() : '';
  if (uid !== '' && rid !== '' && uid === rid) {
    return { ...user, reportId: null };
  }
  return user;
}

function buildDisplayName(d) {
  const raw = (x) => (x == null || x === 'null' ? '' : String(x).trim());
  const first = raw(d.firstName);
  const last = raw(d.lastName);
  const combined = [first, last].filter(Boolean).join(' ').trim();
  if (combined) return combined;
  const n = raw(d.name);
  if (n) return n;
  return raw(d.email) || raw(d.employeeId) || raw(d.userId) || 'Guest';
}

/** Org API field names vary; prefer first non-blank. */
function pickOrganizationLogoPath(org) {
  if (!org || typeof org !== 'object') return null;
  const candidates = [
    org.companyLogo,
    org.company_logo,
    org.companyUrl,
    org.company_url,
    org.logo,
    org.logoUrl,
    org.logo_url,
    org.organizationLogo,
    org.organization_logo,
    org.imageUrl,
    org.image_url,
    org.companyImageUrl,
    org.company_image_url,
    org.brandLogo,
    org.brand_logo,
  ];
  for (const c of candidates) {
    if (!isBlankLogoValue(c)) return String(c).trim();
  }
  return null;
}

function pickLogoFromLoginPayload(d) {
  if (!d || typeof d !== 'object') return null;
  const candidates = [
    d.companyLogoUrl,
    d.company_logo_url,
    d.companyLogo,
    d.company_logo,
    d.companyUrl,
    d.company_url,
  ];
  for (const c of candidates) {
    if (!isBlankLogoValue(c)) return String(c).trim();
  }
  return null;
}

/** Relative key → S3 base URL; absolute http(s) URL → unchanged; blank → Extra Mile Play logo. */
function buildCompanyLogoDisplayUrl(pathOrUrl) {
  const fallback = process.env.REACT_APP_EM_LOGO;
  if (isBlankLogoValue(pathOrUrl)) return fallback;
  const raw = String(pathOrUrl).trim();
  if (/^https?:\/\//i.test(raw)) return raw;
  const base = String(process.env.REACT_APP_S3BUCKET_URL || '').replace(/\/+$/, '');
  const rel = raw.replace(/^\/+/, '');
  if (!base) return fallback;
  return `${base}/${rel}`;
}

export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      // #region agent log
      console.log('[DEBUG-875681:ALL] loginUser_start', {href:window.location.href,search:window.location.search,ts:Date.now()});
      // #endregion
      const queryParams = new URLSearchParams(window.location.search);
      const otp = queryParams.get('otp');
      const save = queryParams.get('save');
      const demo = queryParams.get('demo');
      const fromMobileApp = isTrueQueryParam(queryParams.get('fromMobileApp'));

      /**
       * IMPORTANT:
       * - `save=true` is only for "resume within the same WebView/tab".
       * - If an OTP link is present, we MUST NOT reuse stored session/user (it can be from an older session),
       *   otherwise the app can show "already played" for a brand-new session.
       */
      if (save && !otp) {
        let stored = readUserFromSessionStorage();
        if (!stored) return rejectWithValue('No saved user data found.');
        // #region agent log
        console.log('[DEBUG-875681:H3] resume_stored_data',{sessionId:stored.sessionId,organizationId:stored.organizationId,userId:stored.userId,sessionIdType:typeof stored.sessionId,orgIdType:typeof stored.organizationId,ts:Date.now()});
        // #endregion
        const persistedFromMobileApp = isTrueQueryParam(String(stored?.fromMobileApp ?? ''));
        const finalFromMobileApp = fromMobileApp || persistedFromMobileApp;
        stored = { ...stored, fromMobileApp: finalFromMobileApp };
        writeUserToSessionStorage(stored);
        dispatch(setSession({ sessionId: stored.sessionId, organizationId: stored.organizationId }));
        dispatch(fetchThemeData({ themeId: null }));
        dispatch(setCompanyLogoUrl(stored.companyLogoUrl));
        return stored;
      }

      let userResponseData;
      let authSource = 'ORG_USER';
      /** Full JSON body from OTP login (reportId sometimes lives next to `data`, not inside). */
      let loginResponseEnvelope = null;
      if (demo) {
        console.log("working with demo now");
        function generateRandomUser() {
          const randomString = 'demo' + Math.floor(Math.random() * 10000);
          return {
            data: {
              data: {
                id: Math.floor(Math.random() * 100000),
                email: `${randomString}@gmail.com`,
                userId: randomString,
                firstName: randomString,
                lastName: randomString,
                employeeId: `EM${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`,
                sessionId: 'demo'+Math.floor(Math.random() * 100000),
                organizationId: 'demo'+Math.floor(Math.random() * 100000),
                gameId: null,
                role:"demobypass",
                companyLogoUrl: process.env.REACT_APP_EM_LOGO,
                backButtonRedirect: process.env.REACT_APP_BASE_URL,
              }
            }
          };
        }
        const response = generateRandomUser();
        userResponseData = response.data.data;
      } else {
        const role = queryParams.get('role');
        if (!otp) return rejectWithValue('OTP is required for login.');

        let apiUrl = process.env.REACT_APP_FIREBASE_LOGIN_API;
        const requestData = { otp };
        if (role === 'GUEST_USER') {
          apiUrl = process.env.REACT_APP_FIREBASE_GUEST_USER_API;
          requestData.role = 'GUEST_USER';
          authSource = 'GUEST_USER';
        }

        const clientSecret =
          process.env.REACT_APP_FIREBASE_CLIENT_SECRET || process.env.REACT_APP_CLIENT_SECRET;
        const headers = {
          'client-secret': clientSecret,
          'Content-Type': 'application/json',
          'Idempotency-Key': uuid4(),
        };

        const response = await axios.post(apiUrl, requestData, { headers });
        if (response.data.message !== 'LOGIN SUCCESS') {
          return rejectWithValue('Login failed');
        }
        loginResponseEnvelope = response.data;
        response.data.data['role'] = authSource;
        userResponseData = response.data.data;
      }

      // #region agent log
      console.log('[DEBUG-875681:H2] userResponseData_received',{userId:userResponseData.userId,id:userResponseData.id,sessionId:userResponseData.sessionId,organizationId:userResponseData.organizationId,hasToken:!!userResponseData.token,ts:Date.now()});
      // #endregion
      const resolvedUserId = String(userResponseData.userId || userResponseData.id || '').trim();
      const normalizedReportId =
        pickReportIdFromLoginPayload(userResponseData, resolvedUserId) ??
        (loginResponseEnvelope ? pickReportIdFromLoginPayload(loginResponseEnvelope, resolvedUserId) : null);

      // Store session and fetch theme
      // #region agent log
      console.log('[DEBUG-875681:H3] before_setSession',{sessionId:userResponseData.sessionId,organizationId:userResponseData.organizationId,sessionIdType:typeof userResponseData.sessionId,orgIdType:typeof userResponseData.organizationId,ts:Date.now()});
      // #endregion
      dispatch(setSession({ sessionId: userResponseData.sessionId, organizationId: userResponseData.organizationId }));
      dispatch(fetchThemeData({ themeId: null }));

      // Build user data to persist
      const userData = {
        userId: userResponseData.userId || userResponseData.id,
        token:
          userResponseData.token ||
          userResponseData.accessToken ||
          userResponseData.access_token ||
          "",
        name: buildDisplayName(userResponseData),
        email: userResponseData.email || userResponseData.employeeId,
        sessionId: userResponseData.sessionId,
        organizationId: userResponseData.organizationId,
        gameId: userResponseData.gameId,
        reportId: normalizedReportId,
        source: demo ? 'DEMO' : authSource,
        expiry: Date.now() + 24 * 60 * 60 * 1000,
        role:userResponseData.role,
        companyLogoUrl: process.env.REACT_APP_EM_LOGO,
        backButtonRedirect: process.env.REACT_APP_BASE_URL,
        gameover: 0,
        fromMobileApp,
      };

      if (!demo) {
        let logoResolved = false;
        const orgId = userResponseData.organizationId;
        if (orgId != null && String(orgId).trim() !== '' && String(orgId).toLowerCase() !== 'null') {
          try {
            // #region agent log
            const t2=Date.now();console.log('[DEBUG-875681:H2] org_api_start',{orgId,ts:t2});
            // #endregion
            const orgRes = await axios.get(`${process.env.REACT_APP_ORG_API}/${String(orgId).trim()}`);
            // #region agent log
            console.log('[DEBUG-875681:H2] org_api_success',{status:orgRes.status,elapsed:Date.now()-t2,ts:Date.now()});
            // #endregion
            if (orgRes.data?.message === 'ORGANIZATIONS_FETCHED_SUCCESSFULLY') {
              const org = orgRes.data?.organization ?? orgRes.data?.data?.organization ?? null;
              const logoPath = pickOrganizationLogoPath(org);
              userData.companyLogoUrl = buildCompanyLogoDisplayUrl(logoPath);
              logoResolved = true;
              // Only set game-detail redirect if gameId is a real value
              const gId = userResponseData.gameId;
              if (gId != null && String(gId).trim() !== '' && String(gId).toLowerCase() !== 'null') {
                userData.backButtonRedirect = `${process.env.REACT_APP_BASE_URL}/game-detail/${String(gId).trim()}`;
              }
            }
          } catch (e2) {
            // #region agent log
            console.log('[DEBUG-875681:H2] org_api_error',{err:e2?.message,ts:Date.now()});
            // #endregion
            /* keep EM logo; optional login payload below */
          }
        }
        if (!logoResolved) {
          const fromLogin = pickLogoFromLoginPayload(userResponseData);
          if (fromLogin) {
            userData.companyLogoUrl = buildCompanyLogoDisplayUrl(fromLogin);
          }
        }
      }

      // #region agent log
      console.log('[DEBUG-875681:H4] before_sanitize_logo',{companyLogoUrl:userData.companyLogoUrl,type:typeof userData.companyLogoUrl,ts:Date.now()});
      // #endregion
      userData.companyLogoUrl = sanitizeCompanyLogoUrl(userData.companyLogoUrl);
      // #region agent log
      console.log('[DEBUG-875681:H4] after_sanitize_logo',{companyLogoUrl:userData.companyLogoUrl,ts:Date.now()});
      // #endregion
      // checkHealthSprintPlayed requires userId + sessionId + organizationId; skip (default gameover=0)
      // when any is missing so mobile/guest logins with partial data are never blocked.
      const safeStrForPlayed = (v) =>
        v != null && String(v).trim() !== '' && String(v).toLowerCase() !== 'null' && String(v).toLowerCase() !== 'undefined';
      const hasPlayedCheckFields =
        safeStrForPlayed(userData.userId) &&
        safeStrForPlayed(userData.sessionId) &&
        safeStrForPlayed(userData.organizationId);
      if (hasPlayedCheckFields) {
        try {
          const playedToken = `${String(userData.sessionId).trim()}&${String(userData.organizationId).trim()}`;
          const playedData = await checkHealthSprintPlayedApi({ userId: userData.userId, token: playedToken });
          userData.gameover = isHealthSprintAlreadyCompleted(playedData) ? 1 : 0;
          // Rejoin: an existing `stages.reportid` for this user/session/org means a report row
          // already exists. Reuse it so the seed step below (and every later sendReport call)
          // does an UPDATE (PUT) against that row instead of INSERT-ing a duplicate on every login.
          const existingReportId = playedData?.reportId;
          if (
            (userData.reportId == null || String(userData.reportId).trim() === '') &&
            existingReportId != null &&
            String(existingReportId).trim() !== ''
          ) {
            userData.reportId = String(existingReportId).trim();
          }
        } catch (playedErr) {
          console.warn('Login: checkHealthSprintPlayed failed', playedErr?.message ?? playedErr);
          userData.gameover = 0;
        }
      } else {
        userData.gameover = 0;
      }

      /** Firebase often returns JWT role `PLAYER`; align `user.role` with auth flow for report seed + Rules. */
      const srcNorm = String(userData.source || '').trim().toUpperCase();
      const roleNorm = String(userData.role || '').trim().toUpperCase();
      if (srcNorm === 'ORG_USER' && (roleNorm === 'PLAYER' || roleNorm === 'PLAYERS' || roleNorm === '')) {
        userData.role = 'ORG_USER';
      }
      if (srcNorm === 'GUEST_USER' && roleNorm !== 'GUEST_USER' && roleNorm !== 'ORG_USER') {
        userData.role = 'GUEST_USER';
      }

      /** OTP/login often omits reportId; seed for org + guest flows (`role` or `source` from authSource). */
      const roleUpper = String(userData.role || '').trim().toUpperCase();
      const sourceUpper = String(userData.source || '').trim().toUpperCase();
      const isOrgOrGuest =
        roleUpper === 'ORG_USER' ||
        roleUpper === 'GUEST_USER' ||
        sourceUpper === 'ORG_USER' ||
        sourceUpper === 'GUEST_USER';
      const needsReportSeed =
        !demo &&
        isOrgOrGuest &&
        (userData.reportId == null || String(userData.reportId).trim() === '');
      const safeStr = (v) => v != null && String(v).trim() !== '' && String(v).toLowerCase() !== 'null' && String(v).toLowerCase() !== 'undefined';
      if (needsReportSeed && safeStr(userData.sessionId) && safeStr(userData.organizationId)) {
        // #region agent log
        console.log('[DEBUG-875681:H1] before_adminSessionToken',{sessionId:userData.sessionId,organizationId:userData.organizationId,ts:Date.now()});
        // #endregion
        const adminSessionToken = `${String(userData.sessionId).trim()}&${String(userData.organizationId).trim()}`;
        // #region agent log
        console.log('[DEBUG-875681:H1] adminSessionToken_created',{adminSessionToken,hasToken:!!userData.token,ts:Date.now()});
        // #endregion
        const authForReports = resolveReportsAuthToken(userData.token, adminSessionToken);
        // #region agent log
        console.log('[DEBUG-875681:H5] authForReports_resolved',{hasAuth:!!authForReports,ts:Date.now()});
        // #endregion
        let skipReportSeed = Number(userData.gameover ?? 0) > 0;
        const seedPayload = {
          sessionId: userData.sessionId,
          role: userData.role,
          token: authForReports,
          name: userData.name,
          email: userData.email || null,
          userId: userData.userId,
          gameId: userData.gameId || '',
          organizationId: userData.organizationId,
          points: 0,
          time: 0,
          reportId: null,
          ans: '',
          user_type: userData.role,
          owner: userData.role,
          /** Guest add/score: minimal body like who_wants_to_be `game/add/guest/score`. */
          guestScoreMinimal:
            roleUpper === 'GUEST_USER' || sourceUpper === 'GUEST_USER',
        };
        const isOrg =
          roleUpper === 'ORG_USER' ||
          sourceUpper === 'ORG_USER';
        const hasUserJwt =
          userData.token != null && String(userData.token).trim() !== '';
        /** ORG + Firebase JWT: `game-server/report/add` expects Bearer — try before guest/score. */
        const tryRegisteredFirst = isOrg && hasUserJwt;

        let rid = null;
        let seedReportResponse = null;

        const parseAndNote = (sres) => {
          const id = extractReportIdFromResponse(sres, userData.userId);
          if (
            process.env.NODE_ENV === 'development' &&
            (id == null || String(id).trim() === '') &&
            sres != null &&
            sres !== true
          ) {
            console.warn('Login: report seed OK but no reportId parsed — check reports API body', sres);
          }
          return id;
        };

        if (!skipReportSeed && tryRegisteredFirst) {
          // #region agent log
          const t4=Date.now();console.log('[DEBUG-875681:H3] report_seed_start_reg',{role:roleUpper,ts:t4});
          // #endregion
          try {
            seedReportResponse = await sendReport({
              ...seedPayload,
              useRegisteredReportApi: true,
            });
            rid = parseAndNote(seedReportResponse);
            // #region agent log
            console.log('[DEBUG-875681:H3] report_seed_reg_ok',{rid,elapsed:Date.now()-t4,ts:Date.now()});
            // #endregion
          } catch (e2) {
            // #region agent log
            console.log('[DEBUG-875681:H3] report_seed_reg_fail',{err:e2?.message,elapsed:Date.now()-t4,ts:Date.now()});
            // #endregion
            console.warn('Login: registered report seed failed', {
              role: roleUpper,
              err: e2?.response?.data ?? e2?.message ?? e2,
            });
          }
          if (rid == null || String(rid).trim() === '') {
            // #region agent log
            const t5=Date.now();console.log('[DEBUG-875681:H3] report_seed_fallback_guest',{ts:t5});
            // #endregion
            try {
              seedReportResponse = await sendReport(seedPayload);
              rid = parseAndNote(seedReportResponse);
              // #region agent log
              console.log('[DEBUG-875681:H3] report_seed_guest_ok',{rid,elapsed:Date.now()-t5,ts:Date.now()});
              // #endregion
            } catch (e) {
              // #region agent log
              console.log('[DEBUG-875681:H3] report_seed_guest_fail',{err:e?.message,elapsed:Date.now()-t5,ts:Date.now()});
              // #endregion
              console.warn('Login: guest report seed failed (after registered)', {
                role: roleUpper,
                source: sourceUpper,
                err: e?.response?.data ?? e?.message ?? e,
              });
            }
          }
        } else if (!skipReportSeed) {
          // #region agent log
          const t6=Date.now();console.log('[DEBUG-875681:H3] report_seed_start_guest',{role:roleUpper,ts:t6});
          // #endregion
          try {
            seedReportResponse = await sendReport(seedPayload);
            rid = parseAndNote(seedReportResponse);
            // #region agent log
            console.log('[DEBUG-875681:H3] report_seed_guest_direct_ok',{rid,elapsed:Date.now()-t6,ts:Date.now()});
            // #endregion
          } catch (e) {
            // #region agent log
            console.log('[DEBUG-875681:H3] report_seed_guest_direct_fail',{err:e?.message,elapsed:Date.now()-t6,ts:Date.now()});
            // #endregion
            console.warn('Login: guest report seed failed', {
              role: roleUpper,
              source: sourceUpper,
              err: e?.response?.data ?? e?.message ?? e,
            });
          }
          if (
            (rid == null || String(rid).trim() === '') &&
            isOrg &&
            authForReports
          ) {
            // #region agent log
            const t7=Date.now();console.log('[DEBUG-875681:H3] report_seed_retry_reg',{ts:t7});
            // #endregion
            try {
              const sresReg = await sendReport({
                ...seedPayload,
                useRegisteredReportApi: true,
              });
              seedReportResponse = sresReg;
              const rid2 = parseAndNote(sresReg);
              if (rid2 != null && String(rid2).trim() !== '') {
                rid = String(rid2).trim();
              }
              // #region agent log
              console.log('[DEBUG-875681:H3] report_seed_retry_reg_ok',{rid2,elapsed:Date.now()-t7,ts:Date.now()});
              // #endregion
            } catch (e2) {
              // #region agent log
              console.log('[DEBUG-875681:H3] report_seed_retry_reg_fail',{err:e2?.message,elapsed:Date.now()-t7,ts:Date.now()});
              // #endregion
              console.warn('Login: registered report seed failed', {
                role: roleUpper,
                err: e2?.response?.data ?? e2?.message ?? e2,
              });
            }
          }
        }
        if (rid != null && String(rid).trim() !== '') {
          userData.reportId = String(rid).trim();
        }
      }

      userData.reportId =
        userData.reportId != null && String(userData.reportId).trim() !== ''
          ? String(userData.reportId).trim()
          : null;
      const coerced = stripReportIdIfSameAsUserId(userData);
      Object.assign(userData, coerced);

      dispatch(setCompanyLogoUrl(userData.companyLogoUrl));
      writeUserToSessionStorage(userData);
      // #region agent log
      console.log('[DEBUG-875681:ALL] loginUser_success',{userId:userData.userId,gameover:userData.gameover,ts:Date.now()});
      // #endregion
      return userData;
    } catch (error) {
      // #region agent log
      console.log('[DEBUG-875681:ALL] loginUser_error',{err:error?.message,ts:Date.now()});
      // #endregion
      return rejectWithValue(error.response?.data?.err?.message || error.message);
    }
  }
);
const initialUser = shouldHydrateFromSavedSession()
  ? readUserFromSessionStorage()
  : null;

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: initialUser,
    status: initialUser ? 'succeeded' : 'idle',
    error: null
  },
  reducers: {
    logout: state => {
      state.user = null;
      clearUserFromSessionStorage();
    },
    /** After first `sendReport` POST, API may return `reportId` — store for subsequent PUTs. */
    mergeReportId: (state, action) => {
      const id = action.payload;
      if (!state.user || id == null || id === '') return;
      const uid = state.user.userId != null ? String(state.user.userId).trim() : '';
      if (uid !== '' && String(id).trim() === uid) return;
      state.user = { ...state.user, reportId: id };
      writeUserToSessionStorage(state.user);
    },
  },
  extraReducers: builder => {
    builder
      .addCase(loginUser.pending, state => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.user = stripReportIdIfSameAsUserId(sanitizeUserCompanyLogo(action.payload));
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      });
  }
});

export const { logout, mergeReportId } = authSlice.actions;
export default authSlice.reducer;
